"use client";

import Link from "next/link";
import { useRouter } from "next/navigation";
import { useState } from "react";
import { authClient, followAuthRedirect } from "@/lib/auth-client";
import { MESSAGE_COMPTE_SUSPENDU } from "@/lib/roles";

const MESSAGES: Record<string, string> = {
  INVALID_EMAIL_OR_PASSWORD: "E-mail ou mot de passe incorrect.",
  USER_ALREADY_EXISTS: "Un compte existe déjà avec cet e-mail.",
  USER_ALREADY_EXISTS_USE_ANOTHER_EMAIL: "Un compte existe déjà avec cet e-mail.",
  PASSWORD_TOO_SHORT: "Mot de passe trop court (10 caractères minimum).",
  PASSWORD_TOO_LONG: "Mot de passe trop long.",
  INVALID_EMAIL: "Adresse e-mail invalide.",
  EMAIL_NOT_VERIFIED: "Adresse e-mail pas encore confirmée : un lien de confirmation vient de vous être envoyé. Ouvrez-le pour vous connecter.",
  EMAIL_PASSWORD_SIGN_UP_DISABLED: "Les inscriptions sont fermées.",
  INVALID_CODE: "Code incorrect.",
  INVALID_TWO_FACTOR_COOKIE: "Délai dépassé : reconnectez-vous.",
  TOO_MANY_ATTEMPTS_REQUEST_NEW_CODE: "Trop d'essais. Reconnectez-vous.",
  INVALID_BACKUP_CODE: "Code de secours incorrect.",
  BANNED_USER: MESSAGE_COMPTE_SUSPENDU,
};

/**
 * Après la connexion : reprend le flux OAuth d'un connecteur (claude.ai) s'il y
 * en a un, sinon ouvre le tableau de bord.
 */
function apresConnexion(data: unknown, router: ReturnType<typeof useRouter>) {
  if (followAuthRedirect(data)) return;
  const query = new URLSearchParams(window.location.search);
  if (query.has("client_id")) {
    window.location.assign(`/api/auth/oauth2/authorize?${query.toString()}`);
    return;
  }
  router.replace("/tableau-de-bord");
  router.refresh();
}

function errorMessage(error: { code?: string; status?: number }) {
  if (error.status === 429) return "Trop de tentatives. Patientez quelques minutes.";
  return (error.code && MESSAGES[error.code]) || "Une erreur est survenue. Réessayez.";
}

export function AuthForm({ mode, motDePasseOublie = false }: { mode: "signin" | "signup"; motDePasseOublie?: boolean }) {
  const router = useRouter();
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [deuxFacteurs, setDeuxFacteurs] = useState(false);
  const [secours, setSecours] = useState(false);
  const [aConfirmer, setAConfirmer] = useState<string | null>(null);

  async function onCode(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    setPending(true);
    const code = String(new FormData(event.currentTarget).get("code") ?? "").replace(/\s/g, "");
    const { data, error: err } = secours
      ? await authClient.twoFactor.verifyBackupCode({ code })
      : await authClient.twoFactor.verifyTotp({ code });
    setPending(false);
    if (err) return setError(errorMessage(err));
    apresConnexion(data, router);
  }

  async function onSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    setPending(true);
    const form = new FormData(event.currentTarget);
    const email = String(form.get("email") ?? "").trim();
    const password = String(form.get("password") ?? "");
    const { data, error: err } =
      mode === "signup"
        ? await authClient.signUp.email({ email, password, name: String(form.get("name") ?? "").trim() || email, callbackURL: "/tableau-de-bord" })
        : await authClient.signIn.email({ email, password, callbackURL: "/tableau-de-bord" });
    setPending(false);
    if (err) return setError(errorMessage(err));
    // Vérification d'adresse exigée : pas de session tant que le lien n'est pas ouvert.
    if (mode === "signup" && !(data as { token?: string | null } | null)?.token) return setAConfirmer(email);
    // Double authentification activée : on demande le code avant d'ouvrir la session.
    if ((data as { twoFactorRedirect?: boolean } | null)?.twoFactorRedirect) return setDeuxFacteurs(true);
    // Connexion lancée par un connecteur (claude.ai) : on reprend le flux OAuth.
    apresConnexion(data, router);
  }

  const input = "field py-2.5";

  if (aConfirmer) {
    return (
      <div className="space-y-4 text-sm">
        <p className="alert alert-success">
          Compte créé. Un lien de confirmation vient d&apos;être envoyé à <strong>{aConfirmer}</strong> : ouvrez-le pour accéder au site.
        </p>
        <p className="text-muted">Rien reçu ? Vérifiez les courriers indésirables, ou connectez-vous : un nouveau lien partira.</p>
        <Link href="/connexion" className="link">Aller à la connexion</Link>
      </div>
    );
  }

  if (deuxFacteurs) {
    return (
      <form onSubmit={onCode} className="space-y-4">
        <label className="block space-y-1">
          <span className="text-sm font-medium">{secours ? "Code de secours" : "Code de votre application d'authentification"}</span>
          <input
            name="code"
            required
            autoFocus
            autoComplete="one-time-code"
            inputMode={secours ? "text" : "numeric"}
            pattern={secours ? undefined : "[0-9 ]{6,7}"}
            maxLength={secours ? 32 : 7}
            className={`${input} text-center font-mono text-lg tracking-[0.3em]`}
          />
        </label>
        {error && (
          <p role="alert" className="alert alert-danger">
            {error}
          </p>
        )}
        <button type="submit" disabled={pending} className="btn btn-primary btn-lg w-full">
          {pending ? "Vérification…" : "Valider"}
        </button>
        <button type="button" onClick={() => setSecours(!secours)} className="link mx-auto block text-sm">
          {secours ? "Utiliser le code de l'application" : "Utiliser un code de secours"}
        </button>
      </form>
    );
  }

  return (
    <form onSubmit={onSubmit} className="space-y-4">
      {mode === "signup" && (
        <label className="block space-y-1">
          <span className="text-sm font-medium">Nom</span>
          <input name="name" autoComplete="name" className={input} maxLength={100} />
        </label>
      )}
      <label className="block space-y-1">
        <span className="text-sm font-medium">E-mail</span>
        <input name="email" type="email" autoComplete="email" required className={input} />
      </label>
      <label className="block space-y-1">
        <span className="text-sm font-medium">Mot de passe</span>
        <input
          name="password"
          type="password"
          required
          minLength={mode === "signup" ? 10 : undefined}
          autoComplete={mode === "signup" ? "new-password" : "current-password"}
          className={input}
        />
        {mode === "signup" && <span className="text-xs text-muted">10 caractères minimum.</span>}
        {mode === "signin" && motDePasseOublie && (
          <Link href="/mot-de-passe-oublie" className="link block text-right text-xs">Mot de passe oublié ?</Link>
        )}
      </label>
      {error && (
        <p role="alert" className="alert alert-danger">
          {error}
        </p>
      )}
      <button
        type="submit"
        disabled={pending}
        className="btn btn-primary btn-lg w-full"
      >
        {pending ? "Un instant…" : mode === "signup" ? "Créer mon compte" : "Se connecter"}
      </button>
      {mode === "signup" && (
        <p className="text-xs text-muted">
          Avant d&apos;utiliser le site, vous accepterez les <Link href="/cgu" className="link">conditions générales</Link> et le{" "}
          <Link href="/sous-traitance" className="link">contrat de sous-traitance</Link> des données de vos clients.
        </p>
      )}
      <p className="text-center text-sm text-muted">
        {mode === "signup" ? (
          <>
            Déjà un compte ? <Link href="/connexion" className="link">Se connecter</Link>
          </>
        ) : (
          <>
            Pas encore de compte ? <Link href="/inscription" className="link">Créer un compte</Link>
          </>
        )}
      </p>
    </form>
  );
}
